import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { useCortexStream } from "./useCortexStream";
import {
  closeCortexModal,
  clearCortexHistory,
  clearPendingPrompt,
  clearStreamNotification,
} from "../../store/actions/chatbot-actions";
import {
  addRecentPrompt,
  toggleFavoritePrompt,
  patchCustomdata,
} from "../../store/actions/assistantPrompts-actions";
import { getUserCustomdataEndpoint } from "../../configuration";
import { getSessionStorageData } from "../../global/utility/Utils";
import { STORAGE } from "../../global/constants/storageConstants";
import UserMessage from "../../pages/Assistant/components/UserMessage";
import AssistantMessage from "../../pages/Assistant/components/AssistantMessage";
import ChatInput from "../../pages/Assistant/components/ChatInput";
import SuggestionChips from "../../pages/Assistant/components/SuggestionChips";
import SparkleIcon from "./SparkleIcon";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  width: min(1100px, 95vw);
  height: 82vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.2s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #e5e7eb;
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cd040b 0%, #ea580c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Title = styled.span`
  font-size: 15px;
  font-weight: 600;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: #f3f4f6;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;
  background-color: #f5f5f5;
`;

const InputWrap = styled.div`
  background-color: #f5f5f5;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 32px 0;
  flex: 1;
  justify-content: center;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cd040b 0%, #ea580c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Greeting = styled.h2`
  font-size: 28px;
  font-weight: 300;
  letter-spacing: -0.3px;
  margin: 0;
`;

const SubGreeting = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    color: #6b7280;
    background: rgba(0, 0, 0, 0.05);
  }
`;

const MessagesEndAnchor = styled.div``;

export default function CortexGlobalModal() {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");

  const isOpen = useSelector((state) => state?.chatbot?.cortexModalOpen);
  const pendingPrompt = useSelector((state) => state?.chatbot?.pendingPrompt);
  const cortexMessages = useSelector(
    (state) => state?.chatbot?.cortexMessages?.messages || []
  );

  const session = useSelector((state) => state?.sessionReducer?.activeSession?.activeSession);

  const userDetails = getSessionStorageData(STORAGE.ACCOUNT_DETAILS);
  const displayName = [userDetails?.firstname, userDetails?.lastname].filter(Boolean).join(" ");
  const favorites = useSelector((state) => state?.assistantPrompts?.favorites ?? []);
  const pendingText = useSelector((state) => state?.assistantPrompts?.pendingText ?? null);
  const favoritesEnabled = window.REACT_APP_FEATURE_PROMPT_FAVORITES === "enabled";
  const favoritesSet = new Set(favorites.map((f) => f.text));

  const handleToggleFavorite = (text) => {
    const accountId = session?.accountId;
    const userId = session?.userId || accountId;
    dispatch(toggleFavoritePrompt(text));
    const willAdd = !favoritesSet.has(text);
    const current = favorites.map((f) => [f.id, f.text]);
    let next;
    if (willAdd) {
      const id = String(Date.now());
      next = current.length < 10 ? [...current, [id, text]] : [...current.slice(1), [id, text]];
    } else {
      next = current.filter(([, t]) => t !== text);
    }
    dispatch(
      patchCustomdata({
        url: getUserCustomdataEndpoint(),
        method: "patch",
        data: {
          accountname: accountId,
          id: userId,
          customdata: { assistant_prompts: Object.fromEntries(next) },
        },
      })
    );
  };

  const { streamingMessage, sendMessage, stopStream } = useCortexStream();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [cortexMessages, streamingMessage.content]);

  // Auto-submit pending prompt from CortexBar
  useEffect(() => {
    if (isOpen && pendingPrompt && !streamingMessage.isStreaming) {
      const text = pendingPrompt;
      dispatch(clearPendingPrompt());

      const history = cortexMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
      sendMessage(text, history);

      if (window.REACT_APP_FEATURE_PROMPT_FAVORITES === "enabled") {
        dispatch(addRecentPrompt(text));
      }
    }
  }, [isOpen, pendingPrompt]);

  // Clear notification when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(clearStreamNotification());
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || streamingMessage.isStreaming) return;
    setInputValue("");

    const history = cortexMessages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
    sendMessage(text, history);

    if (window.REACT_APP_FEATURE_PROMPT_FAVORITES === "enabled") {
      dispatch(addRecentPrompt(text));
    }
  };

  const handleSuggestionSelect = (text) => {
    setInputValue(text);
  };

  const handleClose = () => {
    dispatch(closeCortexModal());
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleClose();
  };

  const isStreaming = streamingMessage.isStreaming;
  const hasMessages = cortexMessages.length > 0;

  return ReactDOM.createPortal(
    <Backdrop onClick={handleBackdropClick} onKeyDown={handleKeyDown} tabIndex={-1}>
      <ModalContainer>
        <ModalHeader>
          <Avatar>
            <SparkleIcon size={12} />
          </Avatar>
          <Title>Video Insights Assistant</Title>
          <ClearButton onClick={() => dispatch(clearCortexHistory())}>
            Clear conversation
          </ClearButton>
          <CloseButton onClick={handleClose}>✕</CloseButton>
        </ModalHeader>

        <ModalBody>
          {!hasMessages && !isStreaming ? (
            <EmptyState>
              <IconCircle>
                <SparkleIcon size={20} />
              </IconCircle>
              <Greeting>Hello{displayName ? `, ${displayName}` : ""}!</Greeting>
              <SubGreeting>
                How can I help you with Video Insights today?
              </SubGreeting>
              <SuggestionChips onSelect={handleSuggestionSelect} />
            </EmptyState>
          ) : (
            <>
              {cortexMessages.map((msg) =>
                msg.role === "user" ? (
                  <UserMessage
                    key={msg.id}
                    message={msg}
                    onToggleFavorite={favoritesEnabled ? handleToggleFavorite : undefined}
                    isFavorited={favoritesEnabled ? favoritesSet.has(msg.content) : false}
                    isPending={favoritesEnabled && pendingText === msg.content}
                  />
                ) : (
                  <AssistantMessage key={msg.id} streamingMessage={msg} />
                )
              )}
              {isStreaming && (
                <AssistantMessage streamingMessage={streamingMessage} />
              )}
            </>
          )}
          <MessagesEndAnchor ref={messagesEndRef} />
        </ModalBody>

        <InputWrap>
          <ChatInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSend={handleSend}
            onStop={stopStream}
            isProcessing={isStreaming}
          />
        </InputWrap>
      </ModalContainer>
    </Backdrop>,
    document.body
  );
}
